import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { T } from '../../messages';
import type { ClientSummary } from '../../wire';

/** How many names the search shows at once. Her book is hundreds; a till is not a directory. */
const SHOWN = 8;

/**
 * **Who this sale is for** — "Walk-in" until she says otherwise.
 *
 * A walk-in settles against the shop's built-in walk-in account (spec §6.5),
 * which is a real client row; the screen just calls it what she calls it. Tap
 * to search her clients by name or phone. A visit being closed out already
 * knows its guest, so there it is a label rather than a control.
 */
export function GuestField({
  clients,
  clientId,
  fixedName,
  onChange,
  t,
}: {
  clients: readonly ClientSummary[];
  clientId: string;
  /** The visit's own guest — shown, not changeable. */
  fixedName?: string | undefined;
  onChange: (clientId: string) => void;
  t: T;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const book = useMemo(
    () => clients.filter((c) => !c.archived && !c.is_walk_in_account),
    [clients],
  );
  const current = book.find((c) => c.client_id === clientId)?.display_name;

  const needle = query.trim().toLowerCase();
  const digits = needle.replace(/\D/g, '');
  const found = useMemo(() => {
    const matching =
      needle === ''
        ? [...book].sort((a, b) => (b.last_visit_at ?? '').localeCompare(a.last_visit_at ?? ''))
        : book.filter(
            (c) =>
              c.display_name.toLowerCase().includes(needle) ||
              (digits.length >= 3 &&
                c.identities.some(
                  (i) => i.identity_kind === 'phone' && i.identity.includes(digits),
                )),
          );
    return matching.slice(0, SHOWN);
  }, [book, needle, digits]);

  if (fixedName !== undefined) {
    return (
      <div className="pos-guest pos-guest--fixed">
        <span className="pos-guest__label">{t('checkout.guest')}</span>
        <strong className="pos-guest__name">{fixedName}</strong>
      </div>
    );
  }

  const choose = (id: string): void => {
    onChange(id);
    setOpen(false);
    setQuery('');
  };

  if (!open) {
    return (
      <button
        type="button"
        className="pos-guest"
        aria-label={t('checkout.guestButton', { name: current ?? t('checkout.walkIn') })}
        onClick={() => setOpen(true)}
      >
        <span className="pos-guest__label">{t('checkout.guest')}</span>
        <strong className="pos-guest__name">{current ?? t('checkout.walkIn')}</strong>
        <span className="pos-guest__change" aria-hidden="true">
          {t('checkout.guestChange')}
        </span>
      </button>
    );
  }

  return (
    <div className="pos-guest pos-guest--open">
      <input
        type="search"
        autoFocus
        aria-label={t('checkout.guestSearchLabel')}
        placeholder={t('checkout.guestSearchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
      />
      <ul className="pos-guest__results">
        <li>
          <button type="button" className="pos-guest__result" onClick={() => choose('')}>
            {t('checkout.walkIn')}
          </button>
        </li>
        {found.map((c) => (
          <li key={c.client_id}>
            <button
              type="button"
              className={`pos-guest__result${c.client_id === clientId ? ' is-current' : ''}`}
              onClick={() => choose(c.client_id)}
            >
              {c.display_name}
            </button>
          </li>
        ))}
        {needle !== '' && found.length === 0 ? (
          <li className="muted pos-guest__none">
            {t('checkout.guestNoMatch', { query: query.trim() })}
          </li>
        ) : null}
      </ul>
      <button type="button" className="link-button" onClick={() => setOpen(false)}>
        {t('common.cancel')}
      </button>
    </div>
  );
}
